import { mriHandoff } from "@/mri-bridge";
import Vue from "vue";
import VueRouter from "vue-router";
import { getTracker } from "@/utils/mixpanel-tracker";
import previewRouter from "./route-preview";
import analyzeRouter from "./route-analyze";
import contractRouter from "./route-contract";
import loginRouter from "./route-login";
import realtorRouter from "./route-realtor";
import reportRouter from "./route-report";
import sciRouter from "./route-sci";
import creditRouter from "./route-credit";
import subscribeRouter from "./route-subscribe";
import withdrawRouter from "./route-withdraw";
import withdrawV2Router from "./route-withdraw-v2";
import seoFeedRouter from "./route-seo";
import guaranteeRouter from "./route-guarantee";
import etcRouter from "./route-etc";
import abRouter from "./route-a-b";
import ticketRouter from "./route-ticket";
import orderRouter from "./route-order";
import eventRouter from "./route-event";
import loanouter from "./route-loan";
import ownerRouter from "./route-owner";
import dobyRouter from "./route-doby";
import insuranceRouter from "./route-insurance";
import userRouter from "./route-user";
import sherlockRouter from "./route-sherlock";
import testingRouter from "./route-testing";
import campaignRouter from "./route-campaign";
import buyRouter from "./route-buy";
import alfredRouter from "./route-alfred";
import { flushBehaviorNow } from "@/utils/behaviorQueue";

Vue.use(VueRouter);

const routes = [
  {
    // 인천 캠페인 종료(NZSC-3218): 단축 URL도 홈으로 redirect (랜딩 미노출)
    path: "/ich",
    redirect: "/",
  },

  //---------------------
  //메뉴
  //---------------------
  {
    path: "/",
    name: "home",
    component: () => import("../views/MainAnalyzeView.vue"),
    redirect: "/analyze",
  },
  {
    path: "/mypage",
    name: "mypage",
    component: () => import("../views/MyPage.vue"),
  },
  {
    path: "/main",
    name: "main",
    component: () => import("../views/MainView.vue"),
  },
  {
    path: "/global",
    name: "global",
    component: () => import("../views/analyze/AnalyzeMainLocaleView.vue"),
  },
  {
    path: "/ione",
    name: "ione",
    component: () => import("../views/ibk/IbkMainView.vue"),
  },

  //---------------------
  //공통
  //---------------------
  {
    path: "/login/error",
    name: "login-error",
    component: () => import("../views/LoginError.vue"),
  },
  {
    path: "/logout",
    name: "logout",
    component: () => import("../views/LogoutView.vue"),
  },

  // IBK
  {
    path: "/mypage-ione",
    name: "mypage-ione",
    component: () => import("../views/ibk/IbkMyPage.vue"),
  },

  //---------------------
  //기타
  //---------------------
  ...etcRouter,

  //---------------------
  //seo 피드
  //---------------------
  ...seoFeedRouter,

  //---------------------
  // 내집스캔 보증
  //---------------------
  ...guaranteeRouter,

  //---------------------
  // 분석퍼널
  //---------------------
  ...analyzeRouter,

  //---------------------
  // 미리보기
  //---------------------
  ...previewRouter,

  //---------------------
  // 계약데이터
  //---------------------
  ...contractRouter,

  //---------------------
  // 로그인
  //---------------------
  ...loginRouter,

  //---------------------
  // 공인중개사
  //---------------------
  ...realtorRouter,

  //---------------------
  // 리포트
  //---------------------
  ...reportRouter,

  //---------------------
  // sci
  //---------------------
  ...sciRouter,

  //---------------------
  // credit (개인신용 조회)
  //---------------------
  ...creditRouter,

  //---------------------
  // 구독
  //---------------------
  ...subscribeRouter,

  //---------------------
  // 회원탈퇴
  //---------------------
  ...withdrawRouter,

  //---------------------
  // 회원탈퇴 V2 (새 UI)
  //---------------------
  ...withdrawV2Router,

  //---------------------
  // a-b테스트
  //---------------------
  ...abRouter,

  //---------------------
  // 이용권,쿠폰
  //---------------------
  ...ticketRouter,

  //---------------------
  // 결제
  //---------------------
  ...orderRouter,

  //---------------------
  // 프리미엄 리포트 (잔금일 입력)
  //---------------------
  {
    path: "/premium-report/balance-date",
    name: "PremiumReportBalanceDate",
    component: () => import("../views/buy-report/BalanceScheduleInput.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/premium-report/balance-complete",
    name: "PremiumReportBalanceComplete",
    component: () => import("../views/buy-report/BalanceScheduleComplete.vue"),
    meta: { requiresAuth: true },
  },

  //---------------------
  // 이벤트
  //---------------------
  ...eventRouter,

  //---------------------
  // 전세대출
  //---------------------
  ...loanouter,

  //---------------------
  // 집주인
  //---------------------
  ...ownerRouter,

  //---------------------
  // 도비
  //---------------------
  ...dobyRouter,

  //---------------------
  // 보험
  //---------------------
  ...insuranceRouter,

  //---------------------
  // 회원
  //---------------------
  ...userRouter,

  //---------------------
  // 셜록 (jasanoneclick.com 도메인에서만 사용, naezipscan.com에서는 리다이렉트)
  //---------------------
  ...sherlockRouter,

  //---------------------
  // 캠페인
  //---------------------
  ...campaignRouter,

  //---------------------
  // 매매
  //---------------------
  ...buyRouter,

  //---------------------
  // AI 계약 상담 (alfred, NZSC-3151/3153)
  //---------------------
  ...alfredRouter,

  //---------------------
  // 테스트
  //---------------------
  ...(process.env.NODE_ENV === "development" ? testingRouter : []),
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

let lastRoutePath = null;
let reportEntryPoint = null;

// NZSC-3481: web-core(Next.js)로 컷오버된 라우트 목록 — vercel.json rewrite와 한 세트.
// Vercel rewrite는 풀 페이지 로드에만 적용되므로, 인앱 $router.push 진입은 여기서
// next(false)+location.href 풀 로드로 전환해 rewrite를 태운다(아니면 Vue 원본이 이중 서빙됨).
// ⚠️ 컷오버 게이트 규칙: 라우트를 컷오버하면 rewrite 추가와 함께 이 목록에도 반드시 추가한다.
// 정본 목록 = naezipscan-web-core migration-map.json (status=live)
const WEB_CORE_MIGRATED_ROUTES = new Set([
  "/main",
  "/doby",
  "/doby/landing",
  "/doby/landing-ad",
  "/doby/list",
  "/doby/reviews",
  "/subscribe",
  "/mypage",
  "/insurance/firstamerican/title-001/landing",
  "/a-b/dateconfirm",
  "/landing/monthly",
  "/tax/danger",
  "/tax/notice",
  "/report/deposit/records",
  "/campaign/seoul",
  "/miso/move",
  "/subscribe/owner-house-complete",
  "/loan/house",
  "/loan/income",
  "/loan/hold",
  "/loan/hold-active",
  "/loan/hold-repay",
  "/loan/property",
  "/loan/age",
  "/loan/condition",
  "/loan/difficult",
  "/analyze",
  "/landing",
  "/guide-summary",
  "/loan/guide/concept",
  "/loan/guide/repayment",
  "/loan/guide/interest",
  "/loan/guide/interest-tip",
  "/loan/guide/limit-tip",
  "/feed/home-frauud",
  "/feed/loan",
  "/feed/insurance",
  "/feed/alarm",
  "/feed/loan-landing",
  "/feed/buy-conflicts",
  "/fraud/landing",
  "/report/compare",
  "/report/compare/list",
  "/credit/indiv/list",
  "/report/contract/date",
  "/report/contract/collateral-cancel",
  "/report/contract/product",
  "/analyze/faq",
  "/analyze/faq-area",
  "/analyze/type",
  "/analyze/period",
  "/analyze/period-detail",
  "/analyze/section",
  "/analyze/area",
  "/analyze/freebase",
  "/analyze/freelogin",
  "/analyze/price",
  "/analyze/date",
  "/analyze/date/living",
  "/analyze/address",
  "/analyze/search",
  // 저위험 1차 배치 (NZSC-3808 컷오버)
  "/subscribe/amount",
  "/login/error",
  "/subscribe/card-change-complete",
  "/report/deposit/enter",
  "/withdraw-v2/complete",
  // 저위험 2차 배치 (NZSC-3817 컷오버)
  "/report-preview",
  "/a-b/prevention",
  "/guarantee/information",
  "/guarantee/complete",
  // 저위험 3차 배치 (NZSC-3819 컷오버) — 이관 78/257 = 30.4%
  "/analyze/error",
  "/analyze/extension",
  "/analyze/realtor-price",
  "/analyze/realtor-price-reason",
  "/analyze/faqwrite",
  "/analyze/cash",
  "/creditinfo/error",
  "/analyze/apple/login/cancel",
]);

// 캐싱
let cachedDomainCheck = null;
function getDomainInfo() {
  if (cachedDomainCheck === null) {
    const currentHost = window.location.hostname;
    // endsWith를 사용하여 더 안전하게 체크 (예: fakenaezipscan.com 방지)
    cachedDomainCheck = {
      isNaezipscanDomain:
        currentHost === "naezipscan.com" || currentHost.endsWith(".naezipscan.com"),
      isJasanoneclickDomain:
        currentHost === "jasanoneclick.com" || currentHost.endsWith(".jasanoneclick.com"),
    };
  }
  return cachedDomainCheck;
}

router.beforeEach((to, from, next) => {
  lastRoutePath = from.fullPath;
  if (to.path === "/report" && !reportEntryPoint) {
    reportEntryPoint = from.fullPath;
  }

  // NZSC-3077: 라우트가 실제로 바뀔 때만 exit_screen 발화 (같은 경로 next() X 케이스 제외).
  // tracker singleton에 trackExit 위임 — 페어링은 매니저가 보장.
  if (to.fullPath !== from.fullPath) {
    var tracker = getTracker();
    if (tracker) {
      try {
        tracker.trackExit();
      } catch (e) {
        // tracker 실패가 라우팅을 막으면 안 됨
      }
    }
  }

  // 도메인 체크 캐시 무효화 (매번 최신 상태 확인)
  cachedDomainCheck = null;
  const domainInfo = getDomainInfo();
  const { isNaezipscanDomain, isJasanoneclickDomain } = domainInfo;

  // jasanoneclick.com 도메인 체크 (sherlock 전용 도메인)
  if (isJasanoneclickDomain) {
    // NZSC-3098: query string(utm 등) 붙으면 to.fullPath="/analyze?utm_source=..."가 되어
    // 기존 `to.fullPath === "/analyze"` 비교 실패 → isRootLike=false → 아래 Line 310 분기로
    // 빠져 naezipscan.com 으로 redirect 되는 버그. 카카오 알림톡(utm_source=kakaotalk 필수)을
    // 비롯한 모든 마케팅 캠페인 URL 영향. → to.path 검사로 정정 (query 무관 매치).
    const isRootLike = to.path === "/" || to.path === "" || to.path === "/analyze";

    // 루트(/) 또는 루트 리다이렉트(/analyze) 접근 시 /sherlock/dashboard로 이동
    if (isRootLike) {
      next(false);
      window.location.replace("https://jasanoneclick.com/sherlock/dashboard");
      return;
    }

    // analyze/login 경로는 예외처리 (sherlock/naezipscan 모두 패스)
    if (to.path === "/analyze/login" || to.path === "/analyze/apple/login") {
      next();
      return;
    }

    // /sherlock이 아닌 경로 접근 시 naezipscan.com으로 리다이렉트
    if (!to.path.startsWith("/sherlock")) {
      next(false);
      window.location.replace(`https://naezipscan.com${to.fullPath}`);
      return;
    }

    next();
    return;
  }

  // naezipscan.com 도메인에서 /sherlock 접근 시 jasanoneclick.com으로 리다이렉트
  // analyze/login 경로는 예외처리 (sherlock/naezipscan 모두 패스)
  if (
    to.path.startsWith("/sherlock") &&
    isNaezipscanDomain &&
    to.path !== "/analyze/login" &&
    to.path !== "/analyze/apple/login"
  ) {
    const targetUrl = `https://jasanoneclick.com${to.fullPath}`;
    window.location.replace(targetUrl);
    return;
  }

  // NZSC-3481: 컷오버 라우트 인앱 진입 → 풀 페이지 로드 전환 (위 목록 주석 참조).
  // from.matched.length === 0 (초기 로드)은 제외 — 초기 로드가 Vue에 도달했다는 건
  // rewrite가 없다는 뜻(롤백 상태)이므로 Vue 원본을 그대로 렌더해야 하고,
  // 여기서 풀 로드를 또 하면 무한 새로고침 루프가 된다.
  // NZSC-3639(홈 컷오버): 판정을 from.matched → "브라우저 주소가 이미 그 경로인가"로 교체.
  // 루트(/)는 라우터 설정상 /analyze 로 redirect 되는데 redirect 는 초기 내비게이션이라
  // from.matched.length === 0 → 옛 조건에서는 가드를 건너뛰어 Vue 홈이 그대로 렌더됐다
  // (컷오버 후 / 로 들어온 유저가 구 화면을 보는 경로). 무한 새로고침 방지 의도는 그대로다:
  // 풀 로드 뒤에는 location.pathname === to.path 라 두 번째 발화가 없고, 롤백(rewrite 제거)
  // 상태에서 /analyze 직접 진입도 같은 이유로 한 번도 발화하지 않는다.
  // X-Ray 동봉 인스턴스는 /web/ 아래의 Vue 원본 화면을 시각화하는 전용 빌드다.
  // 여기서 web-core 루트 URL로 풀 리로드하면 캡처 서버 밖으로 빠져 화면이 사라진다.
  // 일반 고객 웹 빌드(플래그 미설정)는 기존 컷오버 동작을 그대로 유지한다.
  if (
    process.env.VUE_APP_XRAY_INSTANCE !== "1" &&
    WEB_CORE_MIGRATED_ROUTES.has(to.path) &&
    window.location.pathname !== to.path
  ) {
    next(false);
    // NZSC-3797: 페이지가 파기되기 전에 계측을 강제 전송한다. 직전에 큐잉된
    // click_action(하단 탭)·exit_screen 은 pagehide 를 기다리다 그대로 사라졌다.
    flushBehaviorNow();
    // NZSC-3867: MRI 프리뷰 안이면 web-core 브랜치 프리뷰로 넘길 수 있게 먼저 알린다(프로덕션 no-op)
    mriHandoff(to.fullPath);
    window.location.href = to.fullPath;
    return;
  }

  // 정상 진행
  next();
});
export { lastRoutePath, reportEntryPoint };
export default router;
